import React, { useEffect, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { CalendarData } from '../api/client';
import { toLocalDateString, isToday } from '../lib/date';
import { haptic } from '../lib/telegram';
import 'react-calendar/dist/Calendar.css';
import './CalendarView.css';

interface CalendarViewProps {
  calendarData: CalendarData | null;
  onDateSelect: (date: Date) => void;
  onMonthChange: (year: number, month: number) => void;
  selectedDate?: Date | null;
}

const firstDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

export const CalendarView: React.FC<CalendarViewProps> = ({
  calendarData,
  onDateSelect,
  onMonthChange,
  selectedDate
}) => {
  const [value, setValue] = useState<Date>(selectedDate || new Date());
  const [viewDate, setViewDate] = useState<Date>(firstDay(selectedDate || new Date()));
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (selectedDate) setValue(selectedDate);
  }, [selectedDate]);

  const changeMonth = (delta: number) => {
    const next = new Date(viewDate.getFullYear(), viewDate.getMonth() + delta, 1);
    setViewDate(next);
    onMonthChange(next.getFullYear(), next.getMonth() + 1);
    haptic.selection();
  };

  const monthLabel = new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric'
  }).format(viewDate);

  const tileContent = ({ date }: { date: Date }) => {
    const ds = toLocalDateString(date);
    const hasWorkout = calendarData?.workouts.some(w => w.date === ds);
    const hasPlan = calendarData?.plans.some(p => p.date === ds);
    return (
      <div className="calendar-tile-markers">
        {hasWorkout && <span className="marker workout-marker" />}
        {hasPlan && !hasWorkout && <span className="marker plan-marker" />}
      </div>
    );
  };

  const tileClassName = ({ date }: { date: Date }) => {
    const ds = toLocalDateString(date);
    return [
      calendarData?.workouts.some(w => w.date === ds) ? 'has-workout' : '',
      calendarData?.plans.some(p => p.date === ds) ? 'has-plan' : '',
      isToday(ds) ? 'is-today' : ''
    ].filter(Boolean).join(' ');
  };

  return (
    <div
      className="calendar-view"
      onTouchStart={event => {
        const touch = event.touches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
      }}
      onTouchEnd={event => {
        if (!touchStart.current) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - touchStart.current.x;
        const dy = touch.clientY - touchStart.current.y;
        touchStart.current = null;
        if (Math.abs(dx) > 52 && Math.abs(dx) > Math.abs(dy) * 1.3) changeMonth(dx < 0 ? 1 : -1);
      }}
    >
      <div className="calendar-month-nav">
        <button onClick={() => changeMonth(-1)} aria-label="Предыдущий месяц">←</button>
        <div>
          <span>КАЛЕНДАРЬ</span>
          <strong>{monthLabel}</strong>
        </div>
        <button onClick={() => changeMonth(1)} aria-label="Следующий месяц">→</button>
      </div>
      <Calendar
        value={value}
        activeStartDate={viewDate}
        showNavigation={false}
        onChange={next => {
          if (next instanceof Date) {
            setValue(next);
            onDateSelect(next);
            haptic.light();
          }
        }}
        tileContent={tileContent}
        tileClassName={tileClassName}
        locale="ru-RU"
        minDetail="month"
        maxDetail="month"
        showNeighboringMonth={false}
      />
      <div className="calendar-legend">
        <span><i className="legend-workout" /> Тренировка</span>
        <span><i className="legend-plan" /> План</span>
        <small>Листайте горизонтально</small>
      </div>
    </div>
  );
};
